import type { CollectionModalScreenNavigationProp } from '../../../../navigation/types';
import type { CollectionModalProps } from '../components/common/CollectionModal';

import {
    AttributeTooltipModal,
    type AttributeTooltipModalProps,
} from '../components/common/AttributeTooltipModal';

import { useNavigation } from '@react-navigation/native';
import React, {
    createContext,
    useContext,
    useState,
    useRef,
    type Dispatch,
    type SetStateAction,
} from 'react';

export type ContextModalProps = Omit<CollectionModalProps, 'onExit' | 'setData'>;

// Separate callbacks from serializable data
type CollectionModalCallbacks = {
    onModalSubmit: CollectionModalProps['onModalSubmit'];
    onModalReset: CollectionModalProps['onModalReset'];
    onClose?: CollectionModalProps['onClose'];
    onCleanup?: () => void;
};

type ContextT = {
    openCollectionModal: (props: ContextModalProps, loggedIn: boolean) => void;
    hasSubmitted: boolean;
    setHasSubmitted: (v: boolean) => void;
    // Store callbacks separately - expose the ref for immediate access
    collectionModalCallbacksRef: React.MutableRefObject<CollectionModalCallbacks>;
    setAttributeTooltipModalProps: Dispatch<SetStateAction<AttributeTooltipModalProps>>;
    dismissAttributeTooltipModal: () => void;
    setModalCleanupCallback: (callback: (() => void) | null) => void;
};

const ModalContext = createContext<ContextT>({
    openCollectionModal: () => undefined,
    hasSubmitted: false,

    setHasSubmitted: () => undefined,
    collectionModalCallbacksRef: {
        current: {
            onModalSubmit: () => Promise.reject(new Error('No onModalSubmit callback')),
            onModalReset: () => Promise.reject(new Error('No onModalReset callback')),
        },
    },
    setAttributeTooltipModalProps: (q) => q,
    dismissAttributeTooltipModal: () => undefined,
    setModalCleanupCallback: () => undefined,
});

export function useModalContext() {
    return useContext(ModalContext);
}

export const JMUIModalContextProvider: React.FC<{
    children?: React.ReactNode;
}> = ({ children }) => {
    const collectionNavigation = useNavigation<CollectionModalScreenNavigationProp>();
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const [attributeTooltipModalProps, setAttributeTooltipModalProps] =
        useState<AttributeTooltipModalProps>({
            title: '',
            content: null,
            isOpen: false,
            onDismiss: () => undefined,
            labelClose: '',
        });

    // Store callbacks in ref for immediate access
    const collectionModalCallbacksRef = useRef<CollectionModalCallbacks>({
        onModalSubmit: () => Promise.reject(new Error('No onModalSubmit callback')),
        onModalReset: () => Promise.reject(new Error('No onModalReset callback')),
    });

    const dismissAttributeTooltipModal = () => {
        setAttributeTooltipModalProps((prev) => ({ ...prev, isOpen: false }));
    };

    const setModalCleanupCallback = (callback: (() => void) | null) => {
        collectionModalCallbacksRef.current.onCleanup = callback || undefined;
    };

    const openCollectionModal = (modalProps: ContextModalProps, loggedIn: boolean) => {
        if (loggedIn) {
            // Extract callbacks from props
            const { onModalSubmit, onModalReset, onClose, ...serializableProps } = modalProps;

            // Store callbacks in ref (synchronous update)
            const callbacks = {
                onModalSubmit,
                onModalReset,
                onClose,
                onCleanup: collectionModalCallbacksRef.current.onCleanup,
            };
            collectionModalCallbacksRef.current = callbacks;

            // Navigate with only serializable props
            collectionNavigation.navigate('CollectionModal', serializableProps);
        }
    };

    return (
        <ModalContext.Provider
            value={{
                openCollectionModal,
                hasSubmitted,
                setHasSubmitted,
                collectionModalCallbacksRef,
                setAttributeTooltipModalProps,
                dismissAttributeTooltipModal,
                setModalCleanupCallback,
            }}
        >
            {children}
            <AttributeTooltipModal {...attributeTooltipModalProps} />
        </ModalContext.Provider>
    );
};
